import { useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "./icons";
import { useAuth } from "../context/AuthContext";
import useAutoplayVideo from "../hooks/useAutoplayVideo";
import { useLanguage } from "../context/LanguageContext";

const EMAIL_PATTERN = "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$";

type Mode = "login" | "register" | "forgot";

export default function LoginPage() {
  const { t } = useLanguage();
  const { user, isLoading, login, register, requestPasswordReset, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  useAutoplayVideo(videoRef);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [loginErrorCode, setLoginErrorCode] = useState<string | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);

  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const matched = confirmPassword.length > 0 && password === confirmPassword && password.length >= 6;

  if (!isLoading && user) {
    return <Navigate to="/" replace />;
  }

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setResetRequested(false);
    setRegistered(false);
    setResendSent(false);
    setLoginErrorCode(undefined);
  };

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoginErrorCode(undefined);
    setResendSent(false);
    setIsSubmitting(true);
    const result = await login(email, password);
    setIsSubmitting(false);
    if (result.ok) {
      navigate("/");
    } else {
      setError(result.error);
      setLoginErrorCode(result.code);
    }
  };

  const handleRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("authPasswordMismatch"));
      return;
    }

    setIsSubmitting(true);
    const result = await register({ email, password, name, phone, company });
    setIsSubmitting(false);
    if (result.ok) {
      setRegistered(true);
    } else {
      setError(result.code === "EMAIL_SEND_FAILED" ? t("authRegisterEmailSendError") : result.error);
    }
  };

  const handleResend = async () => {
    await resendVerification(email);
    setResendSent(true);
  };

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    const result = await requestPasswordReset(email);
    setIsSubmitting(false);
    if (result.ok) {
      setResetRequested(true);
    } else {
      setError(result.error);
    }
  };

  const inputClass =
    "border border-white/25 bg-white/5 px-3 py-2.5 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/60";
  const labelClass = "text-sm font-semibold text-white/60";
  const passwordType = showPassword ? "text" : "password";
  const passwordToggleLabel = showPassword ? t("resetHidePassword") : t("resetShowPassword");
  const passwordToggleButton = (
    <button
      type="button"
      onClick={() => setShowPassword((open) => !open)}
      aria-pressed={showPassword}
      aria-label={passwordToggleLabel}
      className="absolute inset-y-0 right-0 flex items-center px-3 text-white/50 transition-colors duration-200 ease-engineered hover:text-white"
    >
      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  );

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-ink pb-16 pt-32 md:pt-36">
      <video
        ref={videoRef}
        onLoadedData={() => setIsVideoLoaded(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-engineered ${
          isVideoLoaded ? "opacity-100" : "opacity-0"
        }`}
        src="/assets/video/hero-culinary.mp4"
        preload="auto"
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
      />
      <div className="absolute inset-0 bg-black/25" />

      <div className="container-industrial relative z-10">
        <div className="mx-auto grid max-w-5xl grid-cols-1 border border-white/15 bg-black/20 backdrop-blur-md lg:grid-cols-2">
          <div className="hidden flex-col justify-end p-10 lg:flex">
            <h1 className="font-display uppercase text-3xl font-normal tracking-[0.3px] leading-[1.1] text-white xl:text-[2.5rem]">
              {t("loginHeroTitle1")}
              <span className="block text-white/90">{t("loginHeroTitle2")}</span>
            </h1>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70">
              {t("loginHeroDesc")}
            </p>
          </div>

          <div className="flex flex-col justify-center p-8 md:p-12">
            {mode !== "forgot" && (
              <div className="relative mb-8 grid grid-cols-2 border-b border-white/20">
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className={`pb-3 text-center text-xs font-semibold uppercase tracking-wide2 transition-colors duration-200 ease-engineered ${
                    mode === "login" ? "text-white" : "text-white/40 hover:text-white/80"
                  }`}
                >
                  {t("authTabLogin")}
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className={`pb-3 text-center text-xs font-semibold uppercase tracking-wide2 transition-colors duration-200 ease-engineered ${
                    mode === "register" ? "text-white" : "text-white/40 hover:text-white/80"
                  }`}
                >
                  {t("authTabRegister")}
                </button>
                <span
                  aria-hidden
                  className={`absolute bottom-0 h-0.5 w-1/2 bg-accent transition-[left] duration-200 ease-engineered ${
                    mode === "register" ? "left-1/2" : "left-0"
                  }`}
                />
              </div>
            )}

            {mode === "login" && (
              <form onSubmit={handleLoginSubmit} className="flex flex-col gap-5">
                <label className="flex flex-col gap-1.5">
                  <span className={labelClass}>{t("authEmailLabel")}</span>
                  <input
                    required
                    autoFocus
                    type="email"
                    value={email}
                    placeholder="name@company.com"
                    onChange={(e) => setEmail(e.target.value)}
                    className={`${inputClass} placeholder-steel-400`}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={labelClass}>{t("authPasswordLabel")}</span>
                  <span className="relative">
                    <input
                      required
                      type={passwordType}
                      value={password}
                      placeholder="••••••••"
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${inputClass} placeholder-steel-400 pr-11 w-full`}
                    />
                    {passwordToggleButton}
                  </span>
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="mt-1 self-start text-xs text-white/50 underline-offset-2 hover:text-accent hover:underline"
                  >
                    {t("authForgotPassword")}
                  </button>
                </label>
                {error && (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-accent">
                      {loginErrorCode === "EMAIL_NOT_VERIFIED" ? t("authEmailNotVerifiedError") : error}
                    </p>
                    {loginErrorCode === "EMAIL_NOT_VERIFIED" && (
                      <button
                        type="button"
                        onClick={handleResend}
                        className="self-start text-xs text-white/50 underline-offset-2 hover:text-accent hover:underline"
                      >
                        {t("authResendVerification")}
                      </button>
                    )}
                    {loginErrorCode === "EMAIL_NOT_VERIFIED" && resendSent && (
                      <p className="text-xs text-white/70">{t("authResendSentMessage")}</p>
                    )}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary mt-2 w-full disabled:opacity-60"
                >
                  {isSubmitting ? "…" : t("authSubmitLogin")}
                </button>
              </form>
            )}

            {mode === "register" && (
              registered ? (
                <div className="flex flex-col gap-5">
                  <h2 className="font-sans text-lg font-semibold tracking-tight text-white">
                    {t("authCheckEmailTitle")}
                  </h2>
                  <p className="text-sm leading-relaxed text-white/70">
                    {t("authCheckEmailMessage").replace("{email}", email)}
                  </p>
                  <button
                    type="button"
                    onClick={handleResend}
                    className="self-start text-sm font-semibold text-white/50 underline-offset-2 hover:text-accent hover:underline"
                  >
                    {t("authResendVerification")}
                  </button>
                  {resendSent && (
                    <p className="text-sm text-white/70">{t("authResendSentMessage")}</p>
                  )}
                </div>
              ) : (
                <form onSubmit={handleRegisterSubmit} className="flex flex-col gap-5">
                  <label className="flex flex-col gap-1.5">
                    <span className={labelClass}>{t("authNameLabel")}</span>
                    <input
                      required
                      autoFocus
                      type="text"
                      value={name}
                      placeholder="Иван Иванов"
                      onChange={(e) => setName(e.target.value)}
                      className={`${inputClass} placeholder-steel-400`}
                    />
                  </label>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5">
                      <span className={labelClass}>{t("authPhoneLabel")}</span>
                      <input
                        required
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={inputClass}
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={labelClass}>{t("authCompanyLabel")}</span>
                      <input
                        required
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <label className="flex flex-col gap-1.5">
                    <span className={labelClass}>{t("authEmailLabel")}</span>
                    <input
                      required
                      type="email"
                      pattern={EMAIL_PATTERN}
                      title={t("authEmailFormatHint")}
                      value={email}
                      placeholder="name@company.com"
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${inputClass} placeholder-steel-400`}
                    />
                  </label>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5">
                      <span className={labelClass}>{t("authPasswordLabel")}</span>
                      <span className="relative">
                        <input
                          required
                          minLength={6}
                          type={passwordType}
                          value={password}
                          placeholder="••••••••"
                          onChange={(e) => setPassword(e.target.value)}
                          className={`${inputClass} placeholder-steel-400 pr-11 w-full`}
                        />
                        {passwordToggleButton}
                      </span>
                      <span className="text-xs text-white/40">{t("resetPasswordHint")}</span>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className={labelClass}>{t("authConfirmPasswordLabel")}</span>
                      <span className="relative">
                        <input
                          required
                          minLength={6}
                          type={passwordType}
                          value={confirmPassword}
                          placeholder="••••••••"
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className={`${inputClass} placeholder-steel-400 pr-11 w-full ${
                            mismatch ? "border-accent" : matched ? "border-white/60" : ""
                          }`}
                        />
                        {passwordToggleButton}
                      </span>
                      {(mismatch || matched) && (
                        <span className={`text-xs ${mismatch ? "text-accent" : "text-white/60"}`}>
                          {mismatch ? t("authPasswordMismatch") : t("resetPasswordsMatch")}
                        </span>
                      )}
                    </label>
                  </div>
                  {error && <p className="text-sm text-accent">{error}</p>}
                  <button
                    type="submit"
                    disabled={isSubmitting || mismatch}
                    className="btn-primary mt-2 w-full disabled:opacity-60"
                  >
                    {isSubmitting ? "…" : t("authSubmitRegister")}
                  </button>
                </form>
              )
            )}

            {mode === "forgot" && (
              <div className="flex flex-col gap-5">
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="flex items-center gap-1.5 self-start text-sm font-semibold text-white/50 hover:text-accent"
                >
                  <ArrowLeft size={14} />
                  {t("authBackToLogin")}
                </button>

                {resetRequested ? (
                  <p className="text-sm leading-relaxed text-white/70">
                    {t("authResetSentMessage").replace("{email}", email)}
                  </p>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="flex flex-col gap-5">
                    <p className="text-sm leading-relaxed text-white/70">
                      {t("authForgotIntro")}
                    </p>
                    <label className="flex flex-col gap-1.5">
                      <span className={labelClass}>{t("authEmailLabel")}</span>
                      <input
                        required
                        autoFocus
                        type="email"
                        pattern={EMAIL_PATTERN}
                        title={t("authEmailFormatHint")}
                        value={email}
                        placeholder="name@company.com"
                        onChange={(e) => setEmail(e.target.value)}
                        className={`${inputClass} placeholder-steel-400`}
                      />
                    </label>
                    {error && <p className="text-sm text-accent">{error}</p>}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary mt-2 w-full disabled:opacity-60"
                    >
                      {isSubmitting ? "…" : t("authSubmitSendLink")}
                    </button>
                  </form>
                )}
              </div>
            )}

            
          </div>
        </div>
      </div>
    </section>
  );
}
